<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import TemplateSettings from './components/TemplateSettings.vue';
import PortalCard from './components/PortalCard.vue';
import { eventAttributePickerOptions } from './lib/eventAttributePickerOptions';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const records = computed(
  () => store.getters['planningEventPortals/getRecords'] || []
);
const loading = computed(
  () => store.getters['planningEventPortals/getUIFlags']?.isFetching || false
);

const formDialogRef = ref(null);
const deleteDialogRef = ref(null);
const editingId = ref(null);
const saving = ref(false);
const deleteTargetId = ref(null);
const generatingThumbnails = ref(false);

const form = reactive({
  name: '',
  portal_editable_attribute_keys: [],
  theme: {
    accent_color: '#7c3aed',
    bg_color: '#ffffff',
    text_color: '#1e293b',
    font: 'Inter',
    style: 'modern',
    border_radius: 'md',
    spacing: 'normal',
  },
  is_default: false,
});

const resetForm = () => {
  form.name = '';
  form.portal_editable_attribute_keys = [];
  form.theme = {
    accent_color: '#7c3aed',
    bg_color: '#ffffff',
    text_color: '#1e293b',
    font: 'Inter',
    style: 'modern',
    border_radius: 'md',
    spacing: 'normal',
  };
  form.is_default = false;
  editingId.value = null;
};

const openCreateForm = async () => {
  resetForm();
  await nextTick();
  formDialogRef.value?.open();
};

const openEditForm = async template => {
  form.name = template.name;
  form.portal_editable_attribute_keys = [
    ...(template.portal_editable_attribute_keys || []),
  ];
  form.theme = {
    accent_color: '#7c3aed',
    bg_color: '#ffffff',
    text_color: '#1e293b',
    font: 'Inter',
    style: 'modern',
    border_radius: 'md',
    spacing: 'normal',
    ...(template.theme || {}),
  };
  form.is_default = !!template.is_default;
  editingId.value = template.id;
  await nextTick();
  formDialogRef.value?.open();
};

const closeForm = () => {
  formDialogRef.value?.close();
  resetForm();
};

const saveTemplate = async () => {
  if (!form.name) return;
  saving.value = true;
  try {
    const payload = {
      name: form.name,
      portal_editable_attribute_keys: form.portal_editable_attribute_keys,
      theme: form.theme,
      is_default: form.is_default,
    };
    if (editingId.value) {
      await store.dispatch('planningEventPortals/update', {
        id: editingId.value,
        ...payload,
      });
    } else {
      await store.dispatch('planningEventPortals/create', payload);
    }
    closeForm();
  } finally {
    saving.value = false;
  }
};

const confirmDelete = id => {
  deleteTargetId.value = id;
  deleteDialogRef.value?.open();
};

const executeDelete = async () => {
  if (!deleteTargetId.value) return;
  await store.dispatch('planningEventPortals/delete', deleteTargetId.value);
  deleteTargetId.value = null;
  deleteDialogRef.value?.close();
};

const duplicateTemplate = async id => {
  await store.dispatch('planningEventPortals/duplicate', id);
};

const openEditor = portalId => {
  router.push({
    name: 'settings_planning_event_portal_editor',
    params: { portalId },
  });
};

const generateThumbnail = async id => {
  await store.dispatch('planningEventPortals/generateThumbnail', {
    templateId: id,
  });
  await store.dispatch('planningEventPortals/get');
};

const generateAllThumbnails = async () => {
  if (!records.value.length) return;
  generatingThumbnails.value = true;
  try {
    await Promise.allSettled(
      records.value.map(tpl =>
        store.dispatch('planningEventPortals/generateThumbnail', {
          templateId: tpl.id,
        })
      )
    );
    await store.dispatch('planningEventPortals/get');
  } finally {
    generatingThumbnails.value = false;
  }
};

const eventAttributeOptions = computed(() =>
  eventAttributePickerOptions({
    attributes:
      store.getters['attributes/getAttributesByModel']('event_attribute') || [],
    categories: store.getters['customAttributeCategories/getRecords'] || [],
    t,
  })
);

onMounted(async () => {
  await Promise.all([
    store.dispatch('planningEventPortals/get'),
    store.dispatch('attributes/get'),
    store.dispatch('customAttributeCategories/get'),
  ]);
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t('PLANNING.EVENT_PORTALS.TITLE')"
        :description="t('PLANNING.EVENT_PORTALS.DESCRIPTION')"
        :back-url="null"
      >
        <template #actions>
          <Button
            v-if="records.length"
            variant="faded"
            color-scheme="secondary"
            icon="i-lucide-image"
            :label="t('PLANNING.EVENT_PORTALS.GENERATE_PREVIEWS')"
            :loading="generatingThumbnails"
            @click="generateAllThumbnails"
          />
          <Button
            icon="i-lucide-plus"
            :label="t('PLANNING.EVENT_PORTALS.ADD')"
            @click="openCreateForm"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div v-if="loading" class="flex items-center justify-center py-16">
        <span class="i-lucide-loader-2 size-6 text-n-brand animate-spin" />
      </div>

      <div
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <span class="i-lucide-layout-template size-12 text-n-slate-8 mb-4" />
        <p class="text-sm text-n-slate-11">
          {{ t('PLANNING.EVENT_PORTALS.EMPTY') }}
        </p>
        <Button
          class="mt-4"
          icon="i-lucide-plus"
          :label="t('PLANNING.EVENT_PORTALS.ADD')"
          @click="openCreateForm"
        />
      </div>

      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        <PortalCard
          v-for="portal in records"
          :key="portal.id"
          :portal="portal"
          @open="openEditor"
          @settings="openEditForm"
          @duplicate="duplicateTemplate"
          @delete="confirmDelete"
          @preview="generateThumbnail"
        />
      </div>

      <Dialog
        ref="formDialogRef"
        :title="
          editingId
            ? t('PLANNING.EVENT_PORTALS.EDIT')
            : t('PLANNING.EVENT_PORTALS.ADD')
        "
        :show-cancel-button="true"
        :confirm-button-label="t('PLANNING.EVENT_PORTALS.FORM.SAVE')"
        :disable-confirm-button="!form.name || saving"
        @confirm="saveTemplate"
      >
        <TemplateSettings
          v-model:name="form.name"
          v-model:theme="form.theme"
          v-model:is-default="form.is_default"
          v-model:portal-editable-attribute-keys="
            form.portal_editable_attribute_keys
          "
          :event-attribute-options="eventAttributeOptions"
        />
      </Dialog>

      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="t('PLANNING.EVENT_PORTALS.DELETE_DIALOG.TITLE')"
        :description="t('PLANNING.EVENT_PORTALS.DELETE_DIALOG.DESCRIPTION')"
        :confirm-button-label="
          t('PLANNING.EVENT_PORTALS.DELETE_DIALOG.CONFIRM')
        "
        @confirm="executeDelete"
      />
    </template>
  </SettingsLayout>
</template>
